import { useCallback, useEffect, useMemo, useState } from "react";
import {
  activateUser,
  assignRoles,
  createUser,
  deactivateUser,
  getUser,
  getUserAudit,
  listRoles,
  listUsers,
  removeRole,
  sendUserInvite,
  updateUser,
} from "../../../../api/adminUsersApi";
import { listPartners } from "../../../../api/adminPartnersApi";
import {
  listAdminUserSessions,
  revokeAdminUserSession,
  revokeAllAdminUserSessions,
} from "../../../../api/authSessionsApi";
import AdminStatePanel from "../layout/AdminStatePanel";
import AdminTableContainer from "../layout/AdminTableContainer";
import AdminTableFooter from "../layout/AdminTableFooter";
import RowActionMenu from "../layout/RowActionMenu";
import RequirePermission from "../../../../auth/RequirePermission";
import StatusBadge from "../../intelligence/StatusBadge";
import { useAuth } from "../../../../auth/useAuth";
import { formatDateTime, formatShortDate, formatUserName } from "./adminUserFormatters";

const PAGE_SIZE = 25;

function UserStatusBadge({ isActive }) {
  return (
    <StatusBadge
      label={isActive ? "Active" : "Inactive"}
      tone={isActive ? "confirmed" : "disputed"}
    />
  );
}

function RoleBadges({ roles = [] }) {
  if (!roles.length) {
    return <span className="muted">No roles</span>;
  }

  return (
    <div className="admin-users-badges">
      {roles.map((role) => (
        <StatusBadge key={role.roleCode} label={role.roleName || role.roleCode} tone="neutral" />
      ))}
    </div>
  );
}

function ProviderBadges({ providers = [] }) {
  if (!providers.length) {
    return <span className="muted">—</span>;
  }

  return (
    <div className="admin-users-badges">
      {providers.map((provider) => (
        <StatusBadge
          key={`${provider.provider}-${provider.providerEmail || ""}`}
          label={provider.provider === "google" ? "Google" : provider.provider === "local" ? "Local" : provider.provider}
          tone={provider.provider === "google" ? "medium" : "neutral"}
        />
      ))}
    </div>
  );
}

function loginStatusLabel(user) {
  if (user?.hasPasswordLogin) {
    return "Has Password";
  }

  if (user?.hasGoogleLinked) {
    return "Google Linked";
  }

  if (user?.loginStatus === "invite_pending") {
    return "Invite Pending";
  }

  return "No Login Method";
}

function LoginStatusBadge({ user }) {
  const label = loginStatusLabel(user);
  const tone =
    user?.hasPasswordLogin || user?.hasGoogleLinked ? "confirmed" : "disputed";

  return <StatusBadge label={label} tone={tone} />;
}

function ModalBackdrop({ children, onClose, title, wide = false }) {
  return (
    <div className="admin-re-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className={`admin-re-modal panel-card ${wide ? "admin-re-modal--wide" : ""}`}
        role="dialog"
        aria-labelledby="admin-user-modal-title"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="admin-re-modal-header">
          <div>
            <p className="eyebrow">Users</p>
            <h2 id="admin-user-modal-title">{title}</h2>
          </div>
          <button type="button" className="midpoint-share-button" onClick={onClose}>
            Close
          </button>
        </header>
        <div className="admin-re-modal-body">{children}</div>
      </div>
    </div>
  );
}

const AdminUsersPageContent = ({ onClose }) => {
  const { hasRole, hasPermission } = useAuth();
  const isSuperAdmin = hasRole("SUPER_ADMIN");
  const canViewAudit = hasPermission("system.audit.view");
  const canManagePartners = hasPermission("partners.manage");

  const [users, setUsers] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, pageSize: PAGE_SIZE, total: 0, totalPages: 0 });
  const [rolesCatalog, setRolesCatalog] = useState([]);
  const [search, setSearch] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [providerFilter, setProviderFilter] = useState("");
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [rolesUser, setRolesUser] = useState(null);
  const [detailUser, setDetailUser] = useState(null);
  const [sessionsUser, setSessionsUser] = useState(null);
  const [userSessions, setUserSessions] = useState([]);
  const [auditEntries, setAuditEntries] = useState([]);
  const [modalError, setModalError] = useState("");
  const [modalSaving, setModalSaving] = useState(false);

  const [createForm, setCreateForm] = useState({
    email: "",
    displayName: "",
    firstName: "",
    lastName: "",
    isActive: true,
    emailVerified: false,
    roles: ["VIEWER"],
    sendInvite: true,
  });

  const [editForm, setEditForm] = useState({
    displayName: "",
    firstName: "",
    lastName: "",
    avatarUrl: "",
    isActive: true,
    emailVerified: false,
  });

  const [selectedRoles, setSelectedRoles] = useState([]);
  const [partnerCatalog, setPartnerCatalog] = useState([]);
  const [editPartnerAssignments, setEditPartnerAssignments] = useState([]);

  const roleOptions = useMemo(
    () =>
      rolesCatalog.map((role) => ({
        ...role,
        disabled: role.roleCode === "SUPER_ADMIN" && !isSuperAdmin,
      })),
    [rolesCatalog, isSuperAdmin]
  );

  const loadRolesCatalog = useCallback(async () => {
    try {
      const body = await listRoles();
      setRolesCatalog(body.roles || []);
    } catch {
      setRolesCatalog([]);
    }
  }, []);

  const loadUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      const body = await listUsers({
        q: search.trim() || undefined,
        isActive: isActiveFilter === "" ? undefined : isActiveFilter,
        role: roleFilter || undefined,
        authProvider: providerFilter || undefined,
        page,
        pageSize: PAGE_SIZE,
        sort: "display_name",
        direction: "asc",
      });
      setUsers(body.data || []);
      setPagination(body.pagination || { page: 1, pageSize: PAGE_SIZE, total: 0, totalPages: 0 });
    } catch (error) {
      setUsers([]);
      setErrorMessage(error.message || "Unable to load users.");
    } finally {
      setIsLoading(false);
    }
  }, [search, isActiveFilter, roleFilter, providerFilter, page]);

  useEffect(() => {
    loadRolesCatalog();
  }, [loadRolesCatalog]);

  useEffect(() => {
    if (!canManagePartners) {
      return;
    }

    (async () => {
      try {
        const body = await listPartners({ isActive: true });
        setPartnerCatalog(body.partners || []);
      } catch {
        setPartnerCatalog([]);
      }
    })();
  }, [canManagePartners]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const openEdit = async (userId) => {
    try {
      setModalError("");
      const body = await getUser(userId);
      const user = body.user;
      setEditUser(user);
      setEditForm({
        displayName: user.displayName || "",
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        avatarUrl: user.avatarUrl || "",
        isActive: user.isActive,
        emailVerified: user.emailVerified,
      });
      setEditPartnerAssignments(
        (user.partnerOrganizations || []).map((org) => ({
          partnerOrganizationId: org.partnerOrganizationId,
          partnerRole: org.partnerRole || "member",
          isPrimary: Boolean(org.isPrimary),
        }))
      );
    } catch (error) {
      setErrorMessage(error.message || "Unable to load user.");
    }
  };

  const openSessions = async (userOrId) => {
    const userId = typeof userOrId === "string" ? userOrId : userOrId.userId;
    const email = typeof userOrId === "string" ? "" : userOrId.email;

    try {
      setModalError("");
      const body = await listAdminUserSessions(userId);
      setSessionsUser({ userId, email });
      setUserSessions(body.sessions || []);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load user sessions.");
    }
  };

  const handleAdminRevokeSession = async (session) => {
    if (!sessionsUser) {
      return;
    }

    if (!window.confirm("Revoke this session? The user will be signed out on that device.")) {
      return;
    }

    try {
      setModalSaving(true);
      await revokeAdminUserSession(sessionsUser.userId, session.sessionId);
      setSuccessMessage("Session revoked.");
      await openSessions(sessionsUser.userId);
    } catch (error) {
      setModalError(error.message || "Unable to revoke session.");
    } finally {
      setModalSaving(false);
    }
  };

  const handleAdminRevokeAllSessions = async () => {
    if (!sessionsUser) {
      return;
    }

    if (
      !window.confirm(
        "Revoke all sessions for this user? They will need to sign in again on every device."
      )
    ) {
      return;
    }

    try {
      setModalSaving(true);
      const body = await revokeAllAdminUserSessions(sessionsUser.userId);
      setSuccessMessage(
        body?.revokedCount
          ? `Revoked ${body.revokedCount} session(s).`
          : "No active sessions to revoke."
      );
      await openSessions(sessionsUser.userId);
    } catch (error) {
      setModalError(error.message || "Unable to revoke sessions.");
    } finally {
      setModalSaving(false);
    }
  };

  const openDetail = async (userId) => {
    try {
      setModalError("");
      const body = await getUser(userId);
      setDetailUser(body.user);
      setAuditEntries([]);

      if (canViewAudit) {
        try {
          const auditBody = await getUserAudit(userId, { limit: 20 });
          setAuditEntries(auditBody.entries || []);
        } catch {
          setAuditEntries([]);
        }
      }
    } catch (error) {
      setErrorMessage(error.message || "Unable to load user detail.");
    }
  };

  const openManageRoles = async (userId) => {
    try {
      setModalError("");
      const body = await getUser(userId);
      setRolesUser(body.user);
      setSelectedRoles((body.user.roles || []).map((role) => role.roleCode));
    } catch (error) {
      setErrorMessage(error.message || "Unable to load user roles.");
    }
  };

  const handleCreateSubmit = async (event) => {
    event.preventDefault();
    setModalSaving(true);
    setModalError("");

    try {
      const body = await createUser(createForm);
      setCreateOpen(false);
      setSuccessMessage(
        body?.warning
          ? `User created. ${body.warning}`
          : body?.message || "User created successfully."
      );
      setCreateForm({
        email: "",
        displayName: "",
        firstName: "",
        lastName: "",
        isActive: true,
        emailVerified: false,
        roles: ["VIEWER"],
        sendInvite: true,
      });
      await loadUsers();
    } catch (error) {
      setModalError(error.message || "Unable to save user. Please try again.");
    } finally {
      setModalSaving(false);
    }
  };

  const handleEditSubmit = async (event) => {
    event.preventDefault();
    if (!editUser) {
      return;
    }

    if (
      editUser.isActive &&
      editForm.isActive === false &&
      !window.confirm(
        "Deactivating this user will revoke active sessions and prevent future login. Continue?"
      )
    ) {
      return;
    }

    setModalSaving(true);
    setModalError("");

    try {
      await updateUser(editUser.userId, {
        ...editForm,
        ...(canManagePartners ? { partnerOrganizations: editPartnerAssignments } : {}),
      });
      setEditUser(null);
      setSuccessMessage("User updated successfully.");
      await loadUsers();
    } catch (error) {
      setModalError(error.message || "Unable to save user. Please try again.");
    } finally {
      setModalSaving(false);
    }
  };

  const handleRolesSubmit = async (event) => {
    event.preventDefault();
    if (!rolesUser) {
      return;
    }

    setModalSaving(true);
    setModalError("");

    try {
      const currentCodes = new Set((rolesUser.roles || []).map((role) => role.roleCode));
      const nextCodes = new Set(selectedRoles);

      const toAssign = [...nextCodes].filter((code) => !currentCodes.has(code));
      const toRemove = [...currentCodes].filter((code) => !nextCodes.has(code));

      if (toAssign.length) {
        await assignRoles(rolesUser.userId, toAssign);
      }

      for (const roleCode of toRemove) {
        await removeRole(rolesUser.userId, roleCode);
      }

      setRolesUser(null);
      setSuccessMessage("Roles updated successfully.");
      await loadUsers();
    } catch (error) {
      setModalError(error.message || "Unable to update roles.");
    } finally {
      setModalSaving(false);
    }
  };

  const handleSendInvite = async (user) => {
    if (!user?.isActive) {
      setErrorMessage("Cannot send an invitation to an inactive user.");
      return;
    }

    try {
      setErrorMessage("");
      const body = await sendUserInvite(user.userId);
      setSuccessMessage(body?.message || "Invitation sent.");
      await loadUsers();
      if (detailUser?.userId === user.userId) {
        await openDetail(user.userId);
      }
    } catch (error) {
      setErrorMessage(error.message || "Unable to send invitation.");
    }
  };

  const handleToggleActive = async (user) => {
    const activating = !user.isActive;

    if (
      !activating &&
      !window.confirm(
        "Deactivating this user will revoke active sessions and prevent future login. Continue?"
      )
    ) {
      return;
    }

    try {
      setErrorMessage("");
      if (activating) {
        await activateUser(user.userId);
        setSuccessMessage("User activated.");
      } else {
        await deactivateUser(user.userId);
        setSuccessMessage("User deactivated.");
      }
      await loadUsers();
    } catch (error) {
      setErrorMessage(error.message || "Unable to update user status.");
    }
  };

  const toggleRoleSelection = (roleCode) => {
    setSelectedRoles((current) =>
      current.includes(roleCode)
        ? current.filter((code) => code !== roleCode)
        : [...current, roleCode]
    );
  };

  const kpis = useMemo(() => {
    const total = pagination.total || users.length;
    const active = users.filter((u) => u.isActive).length;
    const inactive = users.filter((u) => !u.isActive).length;
    const noLogin = users.filter(
      (u) => !u.hasPasswordLogin && !u.hasGoogleLinked && u.loginStatus !== "invite_pending"
    ).length;

    return { total, active, inactive, noLogin };
  }, [users, pagination.total]);

  return (
    <div className="admin-dashboard admin-users-dash">
      <header className="admin-dashboard__header">
        <div>
          <h1 className="admin-dashboard__title">Users</h1>
          <p className="admin-dashboard__subtitle">
            Manage accounts, roles, and sign-in access
          </p>
        </div>
        <div className="admin-dashboard__header-actions">
          <button
            type="button"
            className="admin-dashboard__action-btn admin-dashboard__action-btn--primary"
            onClick={() => setCreateOpen(true)}
          >
            Create User
          </button>
          <button
            type="button"
            className="admin-dashboard__action-btn"
            onClick={loadUsers}
            disabled={isLoading}
          >
            Refresh
          </button>
          {onClose && (
            <button
              type="button"
              className="admin-dashboard__action-btn"
              onClick={onClose}
            >
              ← Dashboard
            </button>
          )}
        </div>
      </header>

      {!isLoading && !errorMessage && (
        <section className="admin-dashboard__kpis">
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Total Users</span>
            <span className="admin-dash__kpi-value">{kpis.total}</span>
          </div>
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Active</span>
            <span className="admin-dash__kpi-value admin-dash__kpi-value--green">{kpis.active}</span>
          </div>
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Inactive</span>
            <span className={`admin-dash__kpi-value${kpis.inactive > 0 ? " admin-dash__kpi-value--amber" : ""}`}>
              {kpis.inactive}
            </span>
          </div>
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">No Login Method</span>
            <span className={`admin-dash__kpi-value${kpis.noLogin > 0 ? " admin-dash__kpi-value--red" : ""}`}>
              {kpis.noLogin}
            </span>
          </div>
        </section>
      )}

      {successMessage ? (
        <div className="admin-re-toast admin-re-toast--success" role="status">
          {successMessage}
          <button type="button" className="admin-re-toast__dismiss" onClick={() => setSuccessMessage("")}>
            ×
          </button>
        </div>
      ) : null}

      <section className="admin-dash__card admin-dash__card--wide admin-users-dash__table-card">
        <div className="admin-users-dash__toolbar">
          <div className="admin-users-dash__filters">
            <input
              type="search"
              className="admin-source-controls-dash__search"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              placeholder="Email or name…"
            />
            <select
              className="admin-source-controls-dash__select"
              value={isActiveFilter}
              onChange={(event) => {
                setIsActiveFilter(event.target.value);
                setPage(1);
              }}
            >
              <option value="">All statuses</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
            <select
              className="admin-source-controls-dash__select"
              value={roleFilter}
              onChange={(event) => {
                setRoleFilter(event.target.value);
                setPage(1);
              }}
            >
              <option value="">All roles</option>
              {rolesCatalog.map((role) => (
                <option key={role.roleCode} value={role.roleCode}>
                  {role.roleName}
                </option>
              ))}
            </select>
            <select
              className="admin-source-controls-dash__select"
              value={providerFilter}
              onChange={(event) => {
                setProviderFilter(event.target.value);
                setPage(1);
              }}
            >
              <option value="">All providers</option>
              <option value="local">Local</option>
              <option value="google">Google</option>
            </select>
          </div>
        </div>

        {isLoading ? <AdminStatePanel variant="loading" message="Loading users…" /> : null}
        {!isLoading && errorMessage ? (
          <AdminStatePanel variant="error" message={errorMessage} onRetry={loadUsers} />
        ) : null}

        {!isLoading && !errorMessage ? (
          <>
            <AdminTableContainer>
              <table className="admin-data-health-table admin-users-table">
                <thead>
                  <tr>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Roles</th>
                    <th>Providers</th>
                    <th>Login</th>
                    <th>Status</th>
                    <th>Verified</th>
                    <th>Last login</th>
                    <th>Created</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {users.length ? (
                    users.map((user) => (
                      <tr key={user.userId}>
                        <td>{formatUserName(user)}</td>
                        <td>{user.email}</td>
                        <td>
                          <RoleBadges roles={user.roles} />
                        </td>
                        <td>
                          <ProviderBadges providers={user.authProviders} />
                        </td>
                        <td>
                          <LoginStatusBadge user={user} />
                        </td>
                        <td>
                          <UserStatusBadge isActive={user.isActive} />
                        </td>
                        <td>{user.emailVerified ? "Yes" : "No"}</td>
                        <td>{formatDateTime(user.lastLoginAt)}</td>
                        <td>{formatShortDate(user.createdAt)}</td>
                        <td>
                          <div className="admin-re-actions-cell">
                            <button type="button" className="admin-re-inline-link" onClick={() => openDetail(user.userId)}>
                              View
                            </button>
                            <button type="button" className="admin-re-inline-link" onClick={() => openEdit(user.userId)}>
                              Edit
                            </button>
                            <RowActionMenu
                              label="⋯"
                              items={[
                                {
                                  key: "roles",
                                  label: "Manage roles",
                                  onClick: () => openManageRoles(user.userId),
                                },
                                {
                                  key: "sessions",
                                  label: "Sessions",
                                  onClick: () => openSessions(user),
                                },
                                {
                                  key: "toggle",
                                  label: user.isActive ? "Deactivate" : "Activate",
                                  variant: user.isActive ? "danger" : "default",
                                  onClick: () => handleToggleActive(user),
                                },
                                {
                                  key: "invite",
                                  label:
                                    user.loginStatus === "invite_pending"
                                      ? "Send invite"
                                      : "Resend invite",
                                  hidden: !user.isActive,
                                  onClick: () => handleSendInvite(user),
                                },
                              ]}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={10} className="muted">
                        No users match the current filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </AdminTableContainer>

            <AdminTableFooter
              count={pagination.total}
              countLabel="users"
              page={pagination.page}
              totalPages={pagination.totalPages}
              onPrevious={() => setPage((current) => Math.max(1, current - 1))}
              onNext={() => setPage((current) => current + 1)}
            />
          </>
        ) : null}
      </section>

      {createOpen ? (
        <ModalBackdrop title="Create user" onClose={() => setCreateOpen(false)}>
          <form className="admin-users-form" onSubmit={handleCreateSubmit}>
            <p className="muted admin-users-help">
              Users created without a password can sign in with Google when their Google email
              matches this account.
            </p>
            {modalError ? <p className="admin-data-health-error">{modalError}</p> : null}
            <label className="admin-users-field">
              <span>Email</span>
              <input
                type="email"
                required
                value={createForm.email}
                onChange={(event) => setCreateForm((form) => ({ ...form, email: event.target.value }))}
              />
            </label>
            <label className="admin-users-field">
              <span>Display name</span>
              <input
                type="text"
                value={createForm.displayName}
                onChange={(event) =>
                  setCreateForm((form) => ({ ...form, displayName: event.target.value }))
                }
              />
            </label>
            <div className="admin-users-form__split">
              <label className="admin-users-field">
                <span>First name</span>
                <input
                  type="text"
                  value={createForm.firstName}
                  onChange={(event) =>
                    setCreateForm((form) => ({ ...form, firstName: event.target.value }))
                  }
                />
              </label>
              <label className="admin-users-field">
                <span>Last name</span>
                <input
                  type="text"
                  value={createForm.lastName}
                  onChange={(event) =>
                    setCreateForm((form) => ({ ...form, lastName: event.target.value }))
                  }
                />
              </label>
            </div>
            <label className="admin-users-checkbox">
              <input
                type="checkbox"
                checked={createForm.isActive}
                onChange={(event) =>
                  setCreateForm((form) => ({ ...form, isActive: event.target.checked }))
                }
              />
              Active
            </label>
            <label className="admin-users-checkbox">
              <input
                type="checkbox"
                checked={createForm.emailVerified}
                onChange={(event) =>
                  setCreateForm((form) => ({ ...form, emailVerified: event.target.checked }))
                }
              />
              Email verified
            </label>
            <label className="admin-users-checkbox">
              <input
                type="checkbox"
                checked={createForm.sendInvite}
                onChange={(event) =>
                  setCreateForm((form) => ({ ...form, sendInvite: event.target.checked }))
                }
              />
              Send invitation email
            </label>
            <fieldset className="admin-users-roles-fieldset">
              <legend>Initial roles</legend>
              {roleOptions.map((role) => (
                <label key={role.roleCode} className="admin-users-checkbox">
                  <input
                    type="checkbox"
                    disabled={role.disabled}
                    checked={createForm.roles.includes(role.roleCode)}
                    onChange={() => {
                      setCreateForm((form) => {
                        const has = form.roles.includes(role.roleCode);
                        const roles = has
                          ? form.roles.filter((code) => code !== role.roleCode)
                          : [...form.roles, role.roleCode];
                        return { ...form, roles: roles.length ? roles : ["VIEWER"] };
                      });
                    }}
                  />
                  {role.roleName}
                  {role.description ? (
                    <span className="muted admin-users-role-desc"> — {role.description}</span>
                  ) : null}
                </label>
              ))}
            </fieldset>
            <button type="submit" className="report-submit-cta" disabled={modalSaving}>
              {modalSaving ? "Creating…" : "Create user"}
            </button>
          </form>
        </ModalBackdrop>
      ) : null}

      {editUser ? (
        <ModalBackdrop title="Edit user" onClose={() => setEditUser(null)}>
          <form className="admin-users-form" onSubmit={handleEditSubmit}>
            {modalError ? <p className="admin-data-health-error">{modalError}</p> : null}
            <p className="muted">
              <strong>Email:</strong> {editUser.email}
            </p>
            <label className="admin-users-field">
              <span>Display name</span>
              <input
                type="text"
                value={editForm.displayName}
                onChange={(event) =>
                  setEditForm((form) => ({ ...form, displayName: event.target.value }))
                }
              />
            </label>
            <div className="admin-users-form__split">
              <label className="admin-users-field">
                <span>First name</span>
                <input
                  type="text"
                  value={editForm.firstName}
                  onChange={(event) =>
                    setEditForm((form) => ({ ...form, firstName: event.target.value }))
                  }
                />
              </label>
              <label className="admin-users-field">
                <span>Last name</span>
                <input
                  type="text"
                  value={editForm.lastName}
                  onChange={(event) =>
                    setEditForm((form) => ({ ...form, lastName: event.target.value }))
                  }
                />
              </label>
            </div>
            <label className="admin-users-field">
              <span>Avatar URL</span>
              <input
                type="url"
                value={editForm.avatarUrl}
                onChange={(event) =>
                  setEditForm((form) => ({ ...form, avatarUrl: event.target.value }))
                }
              />
            </label>
            <label className="admin-users-checkbox">
              <input
                type="checkbox"
                checked={editForm.isActive}
                onChange={(event) =>
                  setEditForm((form) => ({ ...form, isActive: event.target.checked }))
                }
              />
              Active
            </label>
            <label className="admin-users-checkbox">
              <input
                type="checkbox"
                checked={editForm.emailVerified}
                onChange={(event) =>
                  setEditForm((form) => ({ ...form, emailVerified: event.target.checked }))
                }
              />
              Email verified
            </label>
            {canManagePartners ? (
              <fieldset className="admin-users-roles-fieldset">
                <legend>Partner organizations</legend>
                {editUser.roles?.some((role) => role.roleCode === "PARTNER_BROKER") &&
                !editPartnerAssignments.length ? (
                  <p className="admin-data-health-error">
                    This user has Partner Broker role but no partner assignment.
                  </p>
                ) : null}
                {partnerCatalog.map((partner) => {
                  const assignment = editPartnerAssignments.find(
                    (row) => row.partnerOrganizationId === partner.partnerOrganizationId
                  );
                  const assigned = Boolean(assignment);

                  return (
                    <div key={partner.partnerOrganizationId} className="admin-users-partner-row">
                      <label className="admin-users-checkbox">
                        <input
                          type="checkbox"
                          checked={assigned}
                          onChange={() => {
                            setEditPartnerAssignments((rows) => {
                              if (assigned) {
                                return rows.filter(
                                  (row) =>
                                    row.partnerOrganizationId !==
                                    partner.partnerOrganizationId
                                );
                              }
                              return [
                                ...rows,
                                {
                                  partnerOrganizationId: partner.partnerOrganizationId,
                                  partnerRole: "member",
                                  isPrimary: rows.length === 0,
                                },
                              ];
                            });
                          }}
                        />
                        {partner.partnerName} ({partner.partnerCode})
                      </label>
                      {assigned ? (
                        <>
                          <select
                            value={assignment.partnerRole}
                            onChange={(event) => {
                              const partnerRole = event.target.value;
                              setEditPartnerAssignments((rows) =>
                                rows.map((row) =>
                                  row.partnerOrganizationId === partner.partnerOrganizationId
                                    ? { ...row, partnerRole }
                                    : row
                                )
                              );
                            }}
                          >
                            <option value="owner">Owner</option>
                            <option value="admin">Admin</option>
                            <option value="member">Member</option>
                            <option value="viewer">Viewer</option>
                          </select>
                          <label className="admin-users-checkbox">
                            <input
                              type="radio"
                              name="primary-partner"
                              checked={assignment.isPrimary}
                              onChange={() => {
                                setEditPartnerAssignments((rows) =>
                                  rows.map((row) => ({
                                    ...row,
                                    isPrimary:
                                      row.partnerOrganizationId ===
                                      partner.partnerOrganizationId,
                                  }))
                                );
                              }}
                            />
                            Primary
                          </label>
                        </>
                      ) : null}
                    </div>
                  );
                })}
              </fieldset>
            ) : null}
            <dl className="admin-users-meta">
              <div>
                <dt>Last login</dt>
                <dd>{formatDateTime(editUser.lastLoginAt)}</dd>
              </div>
              <div>
                <dt>Providers</dt>
                <dd>
                  <ProviderBadges providers={editUser.authProviders} />
                </dd>
              </div>
              <div>
                <dt>Created</dt>
                <dd>{formatDateTime(editUser.createdAt)}</dd>
              </div>
              <div>
                <dt>Updated</dt>
                <dd>{formatDateTime(editUser.updatedAt)}</dd>
              </div>
            </dl>
            <button type="submit" className="report-submit-cta" disabled={modalSaving}>
              {modalSaving ? "Saving…" : "Save changes"}
            </button>
          </form>
        </ModalBackdrop>
      ) : null}

      {rolesUser ? (
        <ModalBackdrop title="Manage roles" wide onClose={() => setRolesUser(null)}>
          <form className="admin-users-form" onSubmit={handleRolesSubmit}>
            {modalError ? <p className="admin-data-health-error">{modalError}</p> : null}
            <p className="muted">
              {formatUserName(rolesUser)} ({rolesUser.email})
            </p>
            <fieldset className="admin-users-roles-fieldset">
              <legend>Roles</legend>
              {roleOptions.map((role) => (
                <label key={role.roleCode} className="admin-users-checkbox">
                  <input
                    type="checkbox"
                    disabled={role.disabled}
                    checked={selectedRoles.includes(role.roleCode)}
                    onChange={() => toggleRoleSelection(role.roleCode)}
                  />
                  {role.roleName}
                  {role.description ? (
                    <span className="muted admin-users-role-desc"> — {role.description}</span>
                  ) : null}
                </label>
              ))}
            </fieldset>
            <button type="submit" className="report-submit-cta" disabled={modalSaving}>
              {modalSaving ? "Saving…" : "Save roles"}
            </button>
          </form>
        </ModalBackdrop>
      ) : null}

      {sessionsUser ? (
        <ModalBackdrop title="User sessions" wide onClose={() => setSessionsUser(null)}>
          {modalError ? <p className="admin-data-health-error">{modalError}</p> : null}
          <div className="admin-users-detail-actions">
            <button
              type="button"
              className="midpoint-share-button"
              onClick={handleAdminRevokeAllSessions}
              disabled={modalSaving}
            >
              Revoke all sessions
            </button>
          </div>
          {userSessions.length ? (
            <div className="admin-data-health-table-wrap">
              <table className="admin-data-health-table account-security-table">
                <thead>
                  <tr>
                    <th>Device</th>
                    <th>Provider</th>
                    <th>IP</th>
                    <th>Created</th>
                    <th>Last seen</th>
                    <th>Status</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {userSessions.map((session) => (
                    <tr key={session.sessionId}>
                      <td>{session.deviceLabel || "Unknown device"}</td>
                      <td>{session.loginProvider || "—"}</td>
                      <td>{session.ipAddress || "—"}</td>
                      <td>{formatDateTime(session.createdAt)}</td>
                      <td>{formatDateTime(session.lastSeenAt || session.createdAt)}</td>
                      <td>
                        {session.isActive ? (
                          <StatusBadge label="Active" tone="confirmed" />
                        ) : session.isRevoked ? (
                          <StatusBadge label="Revoked" tone="disputed" />
                        ) : (
                          <StatusBadge label="Expired" tone="neutral" />
                        )}
                      </td>
                      <td>
                        {session.isActive ? (
                          <button
                            type="button"
                            className="admin-re-inline-link"
                            disabled={modalSaving}
                            onClick={() => handleAdminRevokeSession(session)}
                          >
                            Revoke
                          </button>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="muted">No sessions found for this user.</p>
          )}
        </ModalBackdrop>
      ) : null}

      {detailUser ? (
        <ModalBackdrop title="User detail" wide onClose={() => setDetailUser(null)}>
          <dl className="admin-users-detail">
            <div>
              <dt>Name</dt>
              <dd>{formatUserName(detailUser)}</dd>
            </div>
            <div>
              <dt>Email</dt>
              <dd>{detailUser.email}</dd>
            </div>
            <div>
              <dt>Status</dt>
              <dd>
                <UserStatusBadge isActive={detailUser.isActive} />
              </dd>
            </div>
            <div>
              <dt>Roles</dt>
              <dd>
                <RoleBadges roles={detailUser.roles} />
              </dd>
            </div>
            <div>
              <dt>Auth providers</dt>
              <dd>
                <ProviderBadges providers={detailUser.authProviders} />
              </dd>
            </div>
            <div>
              <dt>Login status</dt>
              <dd>
                <LoginStatusBadge user={detailUser} />
              </dd>
            </div>
            <div>
              <dt>Last login</dt>
              <dd>{formatDateTime(detailUser.lastLoginAt)}</dd>
            </div>
            <div>
              <dt>Active sessions</dt>
              <dd>{detailUser.activeSessionCount ?? 0}</dd>
            </div>
            <div>
              <dt>Created</dt>
              <dd>{formatDateTime(detailUser.createdAt)}</dd>
            </div>
            <div>
              <dt>Updated</dt>
              <dd>{formatDateTime(detailUser.updatedAt)}</dd>
            </div>
            {detailUser.consentSummary ? (
              <>
                <div>
                  <dt>Consent status</dt>
                  <dd>
                    {detailUser.consentSummary.requiresConsent ? "Action required" : "Up to date"}
                  </dd>
                </div>
                <div>
                  <dt>Terms accepted</dt>
                  <dd>
                    {detailUser.consentSummary.acceptedTermsVersion
                      ? `Version ${detailUser.consentSummary.acceptedTermsVersion}`
                      : "—"}
                  </dd>
                </div>
                <div>
                  <dt>Privacy accepted</dt>
                  <dd>
                    {detailUser.consentSummary.acceptedPrivacyVersion
                      ? `Version ${detailUser.consentSummary.acceptedPrivacyVersion}`
                      : "—"}
                  </dd>
                </div>
                <div>
                  <dt>Last consent</dt>
                  <dd>{formatDateTime(detailUser.consentSummary.lastConsentAt)}</dd>
                </div>
              </>
            ) : null}
          </dl>
          <div className="admin-users-detail-actions">
            <button
              type="button"
              className="midpoint-share-button"
              onClick={() => openSessions(detailUser)}
            >
              Manage sessions
            </button>
            {detailUser.isActive ? (
              <button
                type="button"
                className="midpoint-share-button"
                onClick={() => handleSendInvite(detailUser)}
              >
                {detailUser.loginStatus === "invite_pending" ? "Send invite" : "Resend invite"}
              </button>
            ) : null}
          </div>
          {canViewAudit && auditEntries.length ? (
            <section className="admin-users-audit">
              <h3>Recent audit activity</h3>
              <ul>
                {auditEntries.map((entry) => (
                  <li key={entry.auditLogId}>
                    <strong>{entry.action}</strong>
                    <span className="muted"> — {formatDateTime(entry.createdAt)}</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </ModalBackdrop>
      ) : null}
    </div>
  );
};

const AdminUsersPage = (props) => (
  <RequirePermission permission="users.manage">
    <AdminUsersPageContent {...props} />
  </RequirePermission>
);

export default AdminUsersPage;
